import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authAPI } from '../services/api';
import { getStoredToken, setStoredToken, clearStoredToken } from '../lib/authToken';

const AuthContext = createContext(null);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const EMERGENT_AUTH_URL = 'https://auth.emergentagent.com/';

export function AuthProvider({ children }) {
  const [profiles, setProfiles] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadProfile = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setProfiles(null);
      setIsLoading(false);
      return;
    }
    try {
      const res = await authAPI.me();
      setProfiles(res.profile);
    } catch (err) {
      clearStoredToken();
      setProfiles(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const bootstrap = async () => {
      // Handle Emergent Google OAuth callback (session_id in URL fragment)
      const hash = window.location.hash || '';
      if (hash.includes('session_id=')) {
        const sessionId = new URLSearchParams(hash.replace(/^#/, '')).get('session_id');
        if (sessionId) {
          try {
            const pendingRole = sessionStorage.getItem('ck_signup_role') || 'user';
            const res = await authAPI.googleSession(sessionId, pendingRole);
            sessionStorage.removeItem('ck_signup_role');
            setStoredToken(res.token);
            if (mounted) setProfiles(res.profile);
          } catch (err) {
            console.error('Google auth exchange failed:', err);
          } finally {
            // Remove the session_id fragment from the URL
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
            if (mounted) setIsLoading(false);
          }
          return;
        }
      }
      await loadProfile();
    };

    bootstrap();
    return () => {
      mounted = false;
    };
  }, [loadProfile]);

  /* ---------- AUTH ACTIONS ---------- */

  const signup = async ({ name, email, password, role = 'user', location, categories = [], phone }) => {
    const res = await authAPI.register({ name, email, password, role, location, categories, phone });
    setStoredToken(res.token);
    setProfiles(res.profile);
    return res.profile;
  };

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    setStoredToken(res.token);
    setProfiles(res.profile);
    return res.profile;
  };

  const loginWithGoogle = async (role = 'user') => {
    // Persist intended role so the callback can apply it to new accounts
    try {
      sessionStorage.setItem('ck_signup_role', role);
    } catch {
      // ignore
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    clearStoredToken();
    setProfiles(null);
  };

  const forgotPassword = async (email) => {
    return authAPI.forgotPassword(email);
  };

  const resetPassword = async (token, newPassword) => {
    return authAPI.resetPassword(token, newPassword);
  };

  const updateProfile = async (updates) => {
    if (!profiles) throw new Error('Not authenticated');
    await authAPI.updateProfile(updates);
    const res = await authAPI.me();
    setProfiles(res.profile);
    return res.profile;
  };

  return (
    <AuthContext.Provider
      value={{
        profiles,
        isLoading,
        signup,
        login,
        loginWithGoogle,
        logout,
        forgotPassword,
        resetPassword,
        updateProfile,
        refreshProfile: loadProfile,
        isAuthenticated: !!profiles,
        isAdmin: profiles?.role === 'admin' || profiles?.role === 'lead_chitrakar',
        isArtist: profiles?.role === 'artist',
        isLeadChitrakar: profiles?.role === 'lead_chitrakar',
        isKalakar: profiles?.role === 'kalakar',
        isSupabaseConfigured: true,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
